// The first-run funnel's camera step: the REAL Add Video Source modal.
//
// Not a copy, not a fork — the same `<Add>` the plus menu opens, given the same
// depot-derived props (`room/components/video/add.tsx` is the other caller and
// this mirrors it deliberately), plus one `onboarding` prop that no other
// caller passes. That prop is the whole difference: it relabels the third step,
// reports each panel the user reaches, and replaces Connect controls with the
// funnel's own connection test.
//
// WHY THE MODAL RATHER THAN THE FUNNEL'S OWN SCREENS. The funnel had its own
// discovery list and its own "which camera is this?" form, and they drifted
// from the production ones in ways that only real hardware found: an all-ONVIF
// network showed thirteen cameras in Add Video Source and none in onboarding,
// and the wizard invented a control endpoint the camera was not listening on.
// One component cannot drift from itself.
//
// NO FunnelShell HERE, and that is load-bearing. FunnelShell is a native
// `<dialog>` opened with `showModal()`, which puts it in the browser's top
// layer; `<Add>` is a Radix portal, which is not. Render both and the modal is
// painted behind the shell and cannot be clicked. The gate renders exactly one
// screen at a time, so they never coexist — see gate.tsx.
//
// Per rules: investigate-before-adding, code-comments-required,
// reference-rules-in-comments, ui-architecture (ai.knowledge/global).

import { useDepot } from '#~/depot/hooks.js';
import { useLastRoomIdPref, useLinkedRoom } from '#~/room/hook_last_id.js';
// The room id every descendant of the modal asks for. See the provider below.
import { Context as RoomContext } from '#~/room/provider/context.js';
import { Add } from '#~/video/components/add/index.js';
import { Panel, type OnboardingExit } from '#~/video/components/add/store/index.js';
import type { Room } from '@studio/models/room';
import { dequal } from 'dequal/lite';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { funnelScreenForPanel } from '../add-handoff.js';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';

interface OnboardingAddSourceProps {
  /**
   * Where to open. `discovery` starts on the scan; `manual-entry` opens
   * straight on the video panel, which is where the Discover footer's
   * "Manually Configure an RTSP source" link lands.
   */
  readonly entry: 'discovery' | 'manual-entry';
  /** The workflow finished its last shared step; the funnel takes over. */
  readonly onConfigured: (exit: OnboardingExit) => void;
  /** The user closed the modal without adding anything. */
  readonly onDismiss: () => void;
}

export const OnboardingAddSource = ({
  entry,
  onConfigured,
  onDismiss
}: OnboardingAddSourceProps): ReactElement | null => {
  // BOTH room signals, for the reason useBridgeSteps documents: the cloud
  // lookup cannot start until the identity store settles and then waits up to
  // 30s, while the app has already navigated into the studio using the saved
  // id. Waiting only on the cloud answer left this screen rendering nothing
  // for half a minute after the user pressed Continue.
  const { roomId: linkedRoomId } = useLinkedRoom();
  const [savedRoomId] = useLastRoomIdPref();
  const roomId = linkedRoomId ?? savedRoomId;
  const tracker = useOnboardingTracker();

  // Same shape and the same equality function as the production wrapper, so the
  // panels receive exactly what they receive from the plus menu.
  //
  // Read straight off `rooms` rather than through `Slicer.room`, which is
  // `ensureNotNil` and THROWS for a room that is not in the depot yet. The
  // production wrapper is mounted by the room route, which cannot render
  // before its own room exists; this one is mounted by a gate that sits above
  // the router, so it genuinely can run a render early — and an exception
  // there replaced the whole app with "Unexpected `T_Nil` value."
  const passthru = useDepot((µ) => {
    const room = roomId === undefined ? undefined : µ.rooms?.[roomId as Room['id']];

    return {
      ...pick(room, ['agentIds', 'devices', 'discoveries']),
      appId: µ.appId
    };
  }, dequal);

  const onboarding = useMemo(
    () => ({
      onConfigured,
      // A panel the user reached is a funnel screen they saw. Reported through
      // the shared ENG-4305 tracker for the same reason every other screen is:
      // owning a name is not a reason to skip the context envelope.
      onPanelView: (panel: Panel): void => {
        const screen = funnelScreenForPanel(panel);
        if (screen === undefined) return;

        tracker.trackStepViewed(onboardingContextFor(screen));
      },
      ...(entry === 'manual-entry' ? { initialPanel: Panel.Video } : {})
    }),
    [entry, onConfigured, tracker]
  );

  // The funnel only reaches this screen once the Bridge is up and the studio is
  // linked, so both are normally present. Rendering nothing rather than an
  // empty modal is still the honest answer if they are not: `Provider` seeds
  // `agentId` from `agentIds[0]`, and an empty list would open a workflow with
  // no Bridge to add anything to.
  if (roomId === undefined || passthru.agentIds === undefined || passthru.agentIds.length === 0) {
    return null;
  }

  // THE ROOM CONTEXT, and it is not optional.
  //
  // OnboardingGate is mounted above the router's outlet, so nothing it renders
  // sits inside the room route's provider — while the modal's own subtree does
  // ask for a room. `AddSourceAuthModal` is rendered unconditionally by the
  // workflow's scaffold and reaches `useRoom`, which is `ensureNotNil` on this
  // context: without a value the whole app was replaced by "Unexpected `T_Nil`
  // value." the moment the modal opened (reproduced live 2026-08-28).
  //
  // Supplying it here rather than moving the gate inside the room route keeps
  // the gate where it is deliberately placed — it must also cover the routes
  // that have no room at all.
  return (
    <RoomContext.Provider value={roomId as Room['id']}>
      <Add
        agentIds={passthru.agentIds}
        appId={passthru.appId}
        devices={passthru.devices ?? {}}
        discoveries={passthru.discoveries ?? {}}
        onboarding={onboarding}
        onOpenChange={(open: boolean) => {
          if (!open) onDismiss();
        }}
        roomId={roomId as Room['id']}
      />
    </RoomContext.Provider>
  );
};
